import React, { useState } from "react";
import {
  XMarkIcon,
  BuildingLibraryIcon,
  UsersIcon,
  UserIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  PhoneIcon,
  ArchiveBoxIcon,
} from "@heroicons/react/24/outline";

const InstitutionView = ({ institution, onClose }) => {
  const [activeTab, setActiveTab] = useState("profile");

  if (!institution) return null;

  const getStatusDisplay = (status) => {
    if (!status) return "Unknown";
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getStatusBadgeStyle = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === "active") return "bg-green-100 text-green-700";
    if (statusLower === "inactive") return "bg-gray-100 text-gray-600";
    if (statusLower === "archived") return "bg-gray-200 text-gray-800";
    if (statusLower === "pending") return "bg-yellow-100 text-yellow-700";
    return "bg-gray-100 text-gray-500";
  };

  const getStatusIcon = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === "archived") return <ArchiveBoxIcon className="w-5 h-5 mr-2 text-gray-600" />;
    return <CheckCircleIcon className="w-5 h-5 mr-2 text-blue-600" />;
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-6xl overflow-hidden">

        {/* HEADER */}
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h2 className="text-xl font-semibold">Institution Profile & Structure</h2>
          <button onClick={onClose}>
            <XMarkIcon className="w-6 h-6 text-gray-500 hover:text-gray-700" />
          </button>
        </div>

        {/* TABS */}
        <div className="flex border-b">
          <button
            className={`flex-1 py-3 flex items-center justify-center gap-2 font-medium ${
              activeTab === "profile"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("profile")}
          >
            <BuildingLibraryIcon className="w-5 h-5" />
            Institution Profile
          </button>

          <button
            className={`flex-1 py-3 flex items-center justify-center gap-2 font-medium ${
              activeTab === "structure"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("structure")}
          >
            <UsersIcon className="w-5 h-5" />
            Organizational Structure
          </button>
        </div>

        {/* BODY */}
        <div className="p-6 space-y-6 max-h-[65vh] overflow-y-auto">
          {activeTab === "profile" ? (
            <>
              {/* Institution Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="flex items-center gap-2 text-gray-800 font-semibold mb-4">
                  <BuildingLibraryIcon className="w-5 h-5 text-blue-600" />
                  Institution Information
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <p>
                    <span className="font-medium text-gray-600">Registration Number:</span>{" "}
                    {institution.registration_no || "—"}
                  </p>
                  <p>
                    <span className="font-medium text-gray-600">Institution:</span>{" "}
                    {institution.name || "—"}
                  </p>
                  <p>
                    <span className="font-medium text-gray-600">Address:</span>{" "}
                    {institution.address || "—"}
                  </p>
                  <p>
                    <span className="font-medium text-gray-600">Institution Type:</span>{" "}
                     {institution.institution_type?.name || "—"}
                  </p>
                </div>
              </div>

              {/* Contact Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="flex items-center gap-2 text-gray-800 font-semibold mb-4">
                  <PhoneIcon className="w-5 h-5 text-blue-600" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <p>
                    <span className="font-medium text-gray-600">Contact Person:</span>{" "}
                    {institution.contact_person || "Not Available"}
                  </p>
                  <p>
                    <span className="font-medium text-gray-600">Email:</span>{" "}
                    {institution.email || "Not Available"}
                  </p>
                  <p>
                    <span className="font-medium text-gray-600">Phone:</span>{" "}
                    {institution.contact_number || "Not Available"}
                  </p>
                </div>
              </div>

              {/* Head of Institution */}
              {institution.structures?.some((s) => s.is_head === 1) && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="flex items-center text-md font-semibold mb-3 text-gray-700">
                    <UserIcon className="w-5 h-5 mr-2 text-blue-600" />
                    Head of Institution
                  </h3>
                  {(() => {
                    const head = institution.structures.find((s) => s.is_head === 1);
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <p>
                          <span className="font-medium text-gray-600">Full Name:</span>{" "}
                          {head.member_name || "N/A"}
                        </p>
                        <p>
                          <span className="font-medium text-gray-600">Position Title:</span>{" "}
                          {head.position || "N/A"}
                        </p>
                        <p>
                          <span className="font-medium text-gray-600">Contact Number:</span>{" "}
                          {head.member_contact || "N/A"}
                        </p>
                        <p>
                          <span className="font-medium text-gray-600">Email Address:</span>{" "}
                          {head.member_email || "N/A"}
                        </p>
                        <p>
                          <span className="font-medium text-gray-600">Start Date:</span>{" "}
                          {head.start_date || "N/A"}
                        </p>
                        <p>
                          <span className="font-medium text-gray-600">End Date:</span>{" "}
                          {head.end_date || "Ongoing"}
                        </p>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Members */}
              {institution.structures?.some((s) => s.is_head === 0) && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="flex items-center text-md font-semibold mb-3 text-gray-700">
                    <UsersIcon className="w-5 h-5 mr-2 text-blue-600" />
                    Members
                  </h3>
                  {institution.structures
                    .filter((s) => s.is_head === 0)
                    .map((member, idx) => (
                      <div key={idx} className="border-b border-gray-200 py-2 text-sm">
                        <p>
                          <span className="font-medium text-gray-600">Full Name:</span>{" "}
                          {member.member_name || "N/A"}
                        </p>
                        <p>
                          <span className="font-medium text-gray-600">Position:</span>{" "}
                          {member.position || "N/A"}
                        </p>
                        <p>
                          <span className="font-medium text-gray-600">Contact Number:</span>{" "}
                          {member.member_contact || "N/A"}
                        </p>
                        <p>
                          <span className="font-medium text-gray-600">Email:</span>{" "}
                          {member.member_email || "N/A"}
                        </p>
                        <p>
                          <span className="font-medium text-gray-600">Start Date:</span>{" "}
                          {member.start_date || "N/A"}
                        </p>
                        <p>
                          <span className="font-medium text-gray-600">End Date:</span>{" "}
                          {member.end_date || "Ongoing"}
                        </p>
                      </div>
                    ))}
                </div>
              )}

              {/* Remarks + Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="flex items-center text-md font-semibold mb-3 text-gray-700">
                    <DocumentTextIcon className="w-5 h-5 mr-2 text-blue-600" />
                    Remarks / Justifications
                  </h3>
                  <p className="text-sm text-gray-600">{institution.additionalInfo || "No additional information."}</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="flex items-center text-md font-semibold mb-3 text-gray-700">
                    {getStatusIcon(institution.status)}
                    Operational Status
                  </h3>
                  <p className="text-sm mb-2">
                    <span className="font-medium text-gray-600">Status:</span>{" "}
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeStyle(institution.status)}`}>
                      {getStatusDisplay(institution.status)}
                    </span>
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Date Registered:</span>{" "}
                    {institution.created_at || "N/A"}
                  </p>
                </div>
              </div>
            </>
          ) : (
            /* ORGANIZATIONAL STRUCTURE CHART */
            <div className="flex flex-col items-center relative">
              {institution.structures
                ?.filter((s) => s.is_head === 1)
                .map((head, idx) => (
                  <div key={idx} className="flex flex-col items-center mb-4">
                    <div className="bubble-node flex flex-col p-2 justify-center items-center bg-emerald-100 text-emerald-800 border border-emerald-400 rounded">
                      <h4 className="font-semibold text-sm">{head.member_name || "N/A"}</h4>
                      <p className="text-xs">{head.position || "Head"}</p>
                    </div>

                    {institution.structures?.some((m) => m.is_head === 0) && (
                      <div className="w-px h-6 bg-gray-300"></div>
                    )}
                  </div>
                ))}

              {/* MEMBERS */}
              <div className="flex flex-col items-center">
                {institution.structures
                  ?.filter((s) => s.is_head === 0)
                  .map((member, i, arr) => (
                    <div key={i} className="flex flex-col items-center mb-2">
                      <div className="bubble-node flex flex-col p-2 justify-center items-center bg-gray-100 text-gray-700 border border-gray-300 rounded">
                        <p className="text-sm font-medium">{member.member_name || "N/A"}</p>
                        <p className="text-xs">{member.position || "Member"}</p>
                      </div>
                      {i < arr.length - 1 && <div className="w-px h-4 bg-gray-300"></div>}
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstitutionView;
